import { Controller } from "@hotwired/stimulus";

class SiteStatisticsController extends Controller {
  static targets = [
    'calculateButton',
    'calculateByDateButton',
    'targetDateInput',
    'calculateLoading',
    'monthlySection',
    'monthlyBody',
    'averageReturnRate',
    'totalSalesAmount',
    'lossRate',
    'totalSalesPreview',
    'totalSalesPreviewValue'
  ]

  static values = {
    calculateUrl: String
  }

  calculate() {
    this.fetchStatistics(null);
  }

  calculateByDate() {
    const targetDate = this.targetDateInputTarget.value;
    if (!targetDate) {
      alert('日付を入力してください');
      return;
    }

    this.fetchStatistics(targetDate);
  }

  fetchStatistics(targetDate) {
    this.setButtonsDisabled(true);
    this.calculateLoadingTarget.style.display = 'inline';

    let url = this.calculateUrlValue;
    if (targetDate) {
      url += `?target_date=${encodeURIComponent(targetDate)}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.error || '計算に失敗しました');
          });
        }
        return response.json();
      })
      .then(data => {
        this.averageReturnRateTarget.value = data.average_return_rate;
        this.totalSalesAmountTarget.value = data.total_sales_amount;
        this.lossRateTarget.value = data.loss_rate;

        // 更新された入力欄の背景色を変更して視覚的にわかりやすくする
        this.highlightUpdatedFields();

        // プレビュー更新
        this.updateTotalSalesPreview();

        // 月別統計データがある場合は参考データセクションを表示
        if (data.monthly_statistics && data.monthly_statistics.length > 0) {
          this.renderMonthlyStatistics(data.monthly_statistics);
          this.monthlySectionTarget.style.display = 'block';
        }

        // ボタンとローディング表示を元に戻す
        this.setButtonsDisabled(false);
        this.calculateLoadingTarget.style.display = 'none';
      })
      .catch(error => {
        alert('計算に失敗しました: ' + error.message);
        this.setButtonsDisabled(false);
        this.calculateLoadingTarget.style.display = 'none';
      });
  }

  setButtonsDisabled(disabled) {
    this.calculateButtonTarget.disabled = disabled;
    this.calculateByDateButtonTarget.disabled = disabled;
  }

  highlightUpdatedFields() {
    const fields = [
      this.averageReturnRateTarget,
      this.totalSalesAmountTarget,
      this.lossRateTarget
    ];

    fields.forEach(field => {
      field.style.backgroundColor = '#fff3cd';
      field.style.transition = 'background-color 0.3s ease';

      // 3秒後に背景色を元に戻す
      setTimeout(() => {
        field.style.backgroundColor = '';
      }, 3000);
    });
  }

  updateTotalSalesPreview() {
    const amount = parseInt(this.totalSalesAmountTarget.value, 10) || 0;

    if (amount > 0) {
      const okuValue = this.formatOku(amount);
      this.totalSalesPreviewValueTarget.textContent = okuValue;
      this.totalSalesPreviewTarget.style.display = 'block';
    } else {
      this.totalSalesPreviewTarget.style.display = 'none';
    }
  }

  formatOku(amount) {
    const OKU_YEN = 100000000;
    const valueInOku = amount / OKU_YEN;

    // 100億円以上の場合は整数表示（小数点を含めると5桁以上になるため）
    if (valueInOku >= 100) {
      return Math.round(valueInOku).toString();
    }
    return valueInOku.toFixed(1);
  }

  formatYen(amount) {
    if (amount >= 100000000) {
      return (amount / 100000000).toFixed(1) + '億円';
    } else if (amount >= 10000) {
      return (amount / 10000).toFixed(0) + '万円';
    }
    return amount.toLocaleString() + '円';
  }

  renderMonthlyStatistics(statistics) {
    // 既存の行をクリア
    this.monthlyBodyTarget.innerHTML = '';

    // XSS対策: textContentを使用してDOM操作
    statistics.forEach(stat => {
      const row = document.createElement('tr');

      const monthCell = document.createElement('td');
      monthCell.textContent = stat.month;
      row.appendChild(monthCell);

      const countCell = document.createElement('td');
      countCell.textContent = stat.project_count + '件';
      row.appendChild(countCell);

      const rateCell = document.createElement('td');
      rateCell.textContent = stat.average_return_rate + '%';
      row.appendChild(rateCell);

      const salesCell = document.createElement('td');
      salesCell.textContent = this.formatYen(stat.total_sales_amount);
      row.appendChild(salesCell);

      this.monthlyBodyTarget.appendChild(row);
    });
  }
}

export { SiteStatisticsController };
